import { isEmptyScreen, type Bar, type Cell, type Screen, type Section, type Tone } from "./model.js";

/**
 * Render a screen as terminal output.
 *
 * Deliberately dependency-free: colour is emitted as raw ANSI so this package
 * stays importable from a Worker bundle, where a terminal-colour library has no
 * business being. `color: false` produces plain text for pipes, CI and tests —
 * tests assert on content, never on escape codes.
 *
 * The layout is width-aware but never reflows text: a dashboard that rewraps
 * differently every run is impossible to diff between two runs, which is most
 * of what a terminal dashboard is for.
 */

export interface TuiOptions {
  /** Terminal width. Defaults to 80, clamped to a readable range. */
  width?: number;
  color?: boolean;
}

const ANSI: Record<Tone | "reset" | "bold" | "dim", string> = {
  ok: "[32m",
  info: "[38;5;209m", // Carrier flame, nearest 256-colour
  warn: "[33m",
  critical: "[31m",
  muted: "[90m",
  reset: "[0m",
  bold: "[1m",
  dim: "[2m",
};

function paint(text: string, code: string, color: boolean): string {
  return color ? `${code}${text}${ANSI.reset}` : text;
}

/** Printable width, ignoring ANSI escapes. */
export function displayWidth(text: string): number {
  return text.replace(/\[[0-9;]*m/g, "").length;
}

function pad(text: string, width: number, align: "left" | "right"): string {
  const gap = Math.max(0, width - displayWidth(text));
  return align === "right" ? " ".repeat(gap) + text : text + " ".repeat(gap);
}

function heading(text: string, color: boolean): string {
  return paint(text.toUpperCase(), ANSI.bold, color);
}

function metricsTui(
  items: { label: string; value: string | number; hint?: string; tone?: Tone }[],
  width: number,
  color: boolean,
): string[] {
  if (items.length === 0) return [];
  // One metric per line: a grid that reflows with terminal width cannot be
  // diffed between runs, and these are meant to be watched.
  const labelWidth = Math.min(
    28,
    Math.max(...items.map((m) => m.label.length)),
  );
  return items.map((m) => {
    const label = paint(pad(m.label, labelWidth, "left"), ANSI.muted, color);
    const value = paint(String(m.value), m.tone ? ANSI[m.tone] : ANSI.bold, color);
    const hint = m.hint ? paint(`  ${m.hint}`, ANSI.dim, color) : "";
    return `  ${label}  ${value}${hint}`.slice(0, width + 64);
  });
}

function barsTui(items: Bar[], width: number, color: boolean, empty?: string): string[] {
  if (items.length === 0) return [`  ${paint(empty ?? "Nothing to show.", ANSI.muted, color)}`];
  const ceiling = Math.max(...items.map((b) => b.max ?? b.value), 1);
  const labelWidth = Math.min(24, Math.max(...items.map((b) => b.label.length)));
  const barWidth = Math.max(10, Math.min(40, width - labelWidth - 22));
  return items.map((b) => {
    const ratio = (b.value / (b.max ?? ceiling)) || 0;
    const filled = Math.max(0, Math.min(barWidth, Math.round(ratio * barWidth)));
    const bar = paint("█".repeat(filled), b.tone ? ANSI[b.tone] : ANSI.info, color) +
      paint("░".repeat(barWidth - filled), ANSI.dim, color);
    const label = paint(pad(b.label, labelWidth, "left"), ANSI.muted, color);
    const value = paint(String(b.hint ?? b.value), ANSI.dim, color);
    return `  ${label}  ${bar}  ${value}`;
  });
}

function tableTui(
  section: Extract<Section, { kind: "table" }>,
  width: number,
  color: boolean,
): string[] {
  if (section.rows.length === 0) {
    return [`  ${paint(section.empty ?? "No rows.", ANSI.muted, color)}`];
  }
  const numeric = new Set(section.numeric ?? []);
  const cols = section.columns.length;
  const widths = Array.from({ length: cols }, (_, i) =>
    Math.max(
      section.columns[i]?.length ?? 0,
      ...section.rows.map((r) => String(r[i] ?? "").length),
    ),
  );
  // Trim the widest column first when the table overflows, so narrow
  // identifier columns stay intact.
  let total = widths.reduce((a, b) => a + b + 2, 2);
  while (total > width && Math.max(...widths) > 8) {
    const widest = widths.indexOf(Math.max(...widths));
    widths[widest] -= 1;
    total -= 1;
  }

  const clip = (cell: Cell, i: number): string => {
    const text = String(cell ?? "");
    return text.length > widths[i] ? `${text.slice(0, Math.max(1, widths[i] - 1))}…` : text;
  };

  const header =
    "  " +
    section.columns
      .map((c, i) => paint(pad(clip(c, i), widths[i], numeric.has(i) ? "right" : "left"), ANSI.muted, color))
      .join("  ");
  const rule = "  " + paint(widths.map((w) => "─".repeat(w)).join("  "), ANSI.dim, color);
  const body = section.rows.map(
    (row) =>
      "  " +
      row
        .map((cell, i) => pad(clip(cell, i), widths[i], numeric.has(i) ? "right" : "left"))
        .join("  "),
  );
  return [header, rule, ...body];
}

function keyValueTui(
  items: { label: string; value: string; tone?: Tone }[],
  color: boolean,
): string[] {
  if (items.length === 0) return [];
  const labelWidth = Math.min(30, Math.max(...items.map((kv) => kv.label.length)));
  return items.map(
    (kv) =>
      `  ${paint(pad(kv.label, labelWidth, "left"), ANSI.muted, color)}  ${paint(kv.value, kv.tone ? ANSI[kv.tone] : ANSI.reset, color)}`,
  );
}

const NOTE_PREFIX: Record<Tone, string> = {
  ok: "ok",
  info: "note",
  warn: "warning",
  critical: "critical",
  muted: "note",
};

function sectionTui(section: Section, width: number, color: boolean): string[] {
  const lines: string[] = [];
  if ("title" in section && section.title) lines.push(heading(section.title, color));
  switch (section.kind) {
    case "metrics":
      lines.push(...metricsTui(section.items, width, color));
      break;
    case "bars":
      lines.push(...barsTui(section.items, width, color, section.empty));
      break;
    case "table":
      lines.push(...tableTui(section, width, color));
      break;
    case "keyvalue":
      lines.push(...keyValueTui(section.items, color));
      break;
    case "note":
      lines.push(
        `  ${paint(`${NOTE_PREFIX[section.tone]}:`, ANSI[section.tone], color)} ${section.text}`,
      );
      break;
  }
  lines.push("");
  return lines;
}

/** Render a screen as terminal text. */
export function renderTui(screen: Screen, opts: TuiOptions = {}): string {
  const width = Math.max(40, Math.min(160, opts.width ?? 80));
  const color = opts.color ?? true;

  const lines: string[] = [];
  lines.push(paint(screen.title, ANSI.bold, color));
  if (screen.subtitle) lines.push(paint(screen.subtitle, ANSI.muted, color));
  lines.push(paint("─".repeat(width), ANSI.dim, color));
  lines.push("");

  if (isEmptyScreen(screen)) {
    lines.push(
      `  ${paint("No data came back for this view.", ANSI.warn, color)} That is a real result, not a`,
      "  loading state — check the account scope and credentials.",
      "",
    );
  } else {
    for (const section of screen.sections) lines.push(...sectionTui(section, width, color));
  }

  if (screen.actions?.length) {
    lines.push(heading("next", color));
    for (const a of screen.actions) {
      lines.push(`  ${paint(a.command, ANSI.info, color)}  ${paint(a.label, ANSI.dim, color)}`);
    }
    lines.push("");
  }
  if (screen.footer) lines.push(paint(screen.footer, ANSI.dim, color));

  return lines.join("\n");
}
